import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { IonRouterOutlet, NavController, Platform, PopoverController, ModalController, ActionSheetController } from '@ionic/angular';
import { ApiService } from './services/core/api.service';
import { ThemeService } from './services/theme.service';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { Keyboard } from '@ionic-native/keyboard/ngx';
import { IdbService } from './services/indexdb.service';
import { differenceInMinutes } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import { AuthenticateService } from './services/authenticate.service';
import { FingerprintAIO } from '@ionic-native/fingerprint-aio/ngx';
import { StorageService } from 'src/app/services/core/storage.service';
import { LoginVM } from 'src/app/models/account/login-vm';
import { LoginService } from 'src/app/services/login.service';
import { InvestorService } from 'src/app/services/investor.service';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { StatusBar, Style } from '@capacitor/status-bar';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  isSSOFlow = false;
  private navigationSubscription: Subscription;
  @ViewChild(IonRouterOutlet, { static: true }) routerOutlet: IonRouterOutlet;
  constructor(private platform: Platform, private login_service: LoginService, private investor_service: InvestorService, private keyboard: Keyboard, private api_service: ApiService, private nav_controller: NavController, private theme_service: ThemeService, private router: Router, private popoverController: PopoverController,
    private modalController: ModalController, private storage_service: StorageService,
    private actionSheetController: ActionSheetController, private idb_service: IdbService, private authenticate_service: AuthenticateService, private faio: FingerprintAIO) {
    this.initializeApp();
    this.backButtonEvent();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (event.url.startsWith('/lgnwt/') || event.url.includes('/api/sso/v2/')) {
          this.isSSOFlow = true;
        }
      }
    });
    this.setBackgroundColor();
    this.authAppPosition();
  }

  ngOnInit() {
    console.log('Safe area bottom:', getComputedStyle(document.documentElement).getPropertyValue('padding-bottom'));

    this.platform.ready().then(() => {
      this.autoLoginIfNeeded();
    });

    // Handle router navigation events
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.closeAllOverlays();
      }
    });

    // Handle back button behavior
    this.platform.backButton.subscribeWithPriority(10, async () => {
      await this.closeAllOverlays();
    });
  }

  async setBackgroundColor() {
    await EdgeToEdge.setBackgroundColor({ color: '#ffffff' });
    await StatusBar.setStyle({ style: Style.Dark });
  }

  async autoLoginIfNeeded() {
    if (this.isSSOFlow) return;

    // Check if we already have an active session
    const token = await this.idb_service.getTkn();
    const currentUrl = this.router.url;

    // If session exists and we are not on the root or login page, stay where we are.
    if (token && currentUrl !== '/' && !currentUrl.includes('login-via-otp') && !currentUrl.includes('login')) {
      return;
    }

    // 🛡️ Guard: If we are already on a login page (either OTP or Standard), don't force another redirect
    if (currentUrl.includes('login') || currentUrl.includes('login-via-otp') || currentUrl.includes('login-master')) {
        console.log('Already on a login page. Skipping auto-redirect.');
        return;
    }

    const loginCred = await this.storage_service.getLoginCred();
    const biometricState = await this.idb_service.getBiometricState();

    if (biometricState.isEnabled && biometricState.secretStored && loginCred) {
      this.nav_controller.navigateRoot('/login-via-otp'); // Let login page handle it
      return;
    }
    this.nav_controller.navigateRoot('/login-via-otp');
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  private async closeAllOverlays() {
    const popovers = await this.popoverController.getTop();
    if (popovers) await popovers.dismiss();

    const modals = await this.modalController.getTop();
    if (modals) await modals.dismiss();

    const actionSheets = await this.actionSheetController.getTop();
    if (actionSheets) await actionSheets.dismiss();
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.api_service.dataRefreshTokenFn();
      this.theme_service.setDefaultColorMode();

      // 🛡️ Deep Link Handling for SSO
      if (Capacitor.isNativePlatform()) {
        import('@capacitor/app').then(({ App }) => {
          App.addListener('appUrlOpen', (data: any) => {
            const url = data.url;
            console.log('App opened with URL:', url);
            if (url.includes('/api/sso/v2/')) {
              this.isSSOFlow = true;
              // Extract path and query for internal routing
              const path = url.split('.com')[1] || url.split('://')[1];
              if (path) {
                this.router.navigateByUrl(path);
              }
            }
          });
        });
      }

      if (Capacitor.getPlatform() == 'web') {
        return;
      }
      this.keyboard.hideFormAccessoryBar(true);
      this.keyboard.disableScroll(true);
    });
  }

  backButtonEvent() {
    this.platform.backButton.subscribeWithPriority(10000, () => {
      if (this.routerOutlet.canGoBack()) {
        this.nav_controller.back();
      }
      else {
        navigator['app'].exitApp();
      }
    });
  }

  authAppPosition() {
    this.platform.pause.subscribe(() => {
      this.authenticate_service.updateLastActiveTime();
    });

    this.platform.resume.subscribe(async () => {
      // 🟢 NEW: Delegate to service
      await this.authenticate_service.checkSessionStatus();

      // 🔄 Re-sync Refresh Timer on resume to ensure we stay on the 18-min track
      await this.api_service.dataRefreshTokenFn();

      // 🟢 FIX: Ensure we track activity on resume
      this.authenticate_service.updateLastActiveTime();
    });

    // 🟢 NEW: Listen for Force Logout events
    this.authenticate_service.checkDsData().subscribe((data) => {
      if (data === 'FORCE_LOGOUT') {
        this.logout();
      }
    });
  }

  async logout() {
    // 🟢 UPDATED: Clear ONLY session data, preserve Biometric/AppVersion
    await this.api_service.logoutCredData();
    await this.idb_service.clearSessionDataOnly();
    await this.storage_service.clearLoginData();

    // 🟢 Clear memory caches for fresh relogin
    if (this.login_service) {
      this.login_service.mfPortfolio = null;
      this.login_service.orderBookData = null;
      this.login_service.categoryList = null;
    }
    if (this.investor_service) {
      this.investor_service.sipOverview = null;
    }

    // Do NOT clear localStorage (preserves APP_VERSION)
    sessionStorage.clear();
    this.api_service.showSessionExpiredAlert('Your session has expired due to security reasons. Please login again to continue.');
  }

  async forceLogout(error) {
    if (Capacitor.getPlatform() == 'android') {
      if (error.message == 'BIOMETRIC_DISMISSED' && error.code == '-108') {
        this.nav_controller.navigateRoot(['login-via-otp']);
      }
    }
    else if (Capacitor.getPlatform() == 'ios') {
      if (error.message == 'Authentication canceled.' && error.code == '-108') {
        this.nav_controller.navigateRoot(['login-via-otp']);
      }
    }
  }

  removeUnwantedData() {
    // what all data need to be removed to be added here.
  }
}